import type {
  OrgCustomField,
  OrgCustomFieldDefinition,
  OrgCustomFieldEntityType,
  OrgCustomFieldOverrideMap,
  OrgCustomFieldType,
  OrgCustomFieldValue,
} from '../models/customFields';
import { isPersonNodeType } from '../fields/entityFieldPacks';

const SUPPORTED_ENTITY_TYPES = new Set<OrgCustomFieldEntityType>([
  'person',
  'position',
  'department',
  'team',
  'project',
  'role_profile',
]);

export interface ResolvedOrgCustomField {
  fieldId: string;
  name: string;
  type: OrgCustomFieldType;
  showOnCard: boolean;
  defaultValue?: OrgCustomFieldValue;
  value?: OrgCustomFieldValue;
  hasOverride: boolean;
}

export interface ResolveEffectiveCustomFieldsParams {
  nodeType?: string;
  entityType?: OrgCustomFieldEntityType;
  definitions: OrgCustomFieldDefinition[];
  overrides?: OrgCustomFieldOverrideMap;
}

export interface ResolveEffectiveCustomFieldsResult {
  entityType: OrgCustomFieldEntityType | null;
  fields: ResolvedOrgCustomField[];
  orphanOverrideIds: string[];
}

export interface MigrateLegacyCustomFieldsParams {
  entityType: OrgCustomFieldEntityType;
  definitions: OrgCustomFieldDefinition[];
  legacyCustomFields: OrgCustomField[];
  existingOverrides?: OrgCustomFieldOverrideMap;
  createDefinitionId?: () => string;
  useLegacyValueAsDefault?: boolean;
}

export interface MigrateLegacyCustomFieldsResult {
  nextDefinitions: OrgCustomFieldDefinition[];
  nextOverrides: OrgCustomFieldOverrideMap;
  createdDefinitions: OrgCustomFieldDefinition[];
  orphanOverrideIds: string[];
  migratedCount: number;
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function toCustomFieldType(value: unknown): OrgCustomFieldType {
  if (value === 'number' || value === 'date' || value === 'text') return value;
  return 'text';
}

function toStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isValuePresent(value: OrgCustomFieldValue | undefined): value is OrgCustomFieldValue {
  if (value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function isValueTypeCompatible(value: OrgCustomFieldValue, type: OrgCustomFieldType): boolean {
  if (type === 'number') return typeof value === 'number';
  return typeof value === 'string';
}

function normalizeLegacyField(field: OrgCustomField): OrgCustomField | null {
  const name = toStringOrEmpty(field.name);
  if (!name) return null;
  const id = toStringOrEmpty(field.id) || name;
  const type = toCustomFieldType(field.type);
  const value = field.value;
  const normalized: OrgCustomField = {
    id,
    name,
    type,
    ...(isValuePresent(value) ? { value } : {}),
    ...(typeof field.showOnCard === 'boolean' ? { showOnCard: field.showOnCard } : {}),
  };
  return normalized;
}

function isSupportedEntityType(entityType: string): entityType is OrgCustomFieldEntityType {
  return SUPPORTED_ENTITY_TYPES.has(entityType as OrgCustomFieldEntityType);
}

export function normalizeOrgEntityType(nodeType: string): OrgCustomFieldEntityType | null {
  if (isPersonNodeType(nodeType)) return 'person';
  switch (nodeType) {
    case 'org_position':
      return 'position';
    case 'org_department':
      return 'department';
    case 'org_team':
      return 'team';
    case 'org_project':
      return 'project';
    case 'org_role_profile':
      return 'role_profile';
    default:
      return null;
  }
}

export function resolveOrgEntityType(params: {
  nodeType?: string;
  entityType?: OrgCustomFieldEntityType;
}): OrgCustomFieldEntityType | null {
  if (params.entityType && isSupportedEntityType(params.entityType)) return params.entityType;
  if (params.nodeType) return normalizeOrgEntityType(params.nodeType);
  return null;
}

export function getDefinitionsForEntityType(
  definitions: OrgCustomFieldDefinition[],
  entityType: OrgCustomFieldEntityType
): OrgCustomFieldDefinition[] {
  return definitions.filter((definition) => definition.entityType === entityType);
}

export function detectOrphanOverrideIds(
  overrides: OrgCustomFieldOverrideMap | undefined,
  definitions: OrgCustomFieldDefinition[]
): string[] {
  if (!overrides) return [];
  const definitionIds = new Set(definitions.map((definition) => definition.id));
  return Object.keys(overrides).filter((overrideId) => !definitionIds.has(overrideId));
}

export function removeOrphanOverrides(
  overrides: OrgCustomFieldOverrideMap | undefined,
  definitions: OrgCustomFieldDefinition[]
): { nextOverrides: OrgCustomFieldOverrideMap; orphanOverrideIds: string[] } {
  const orphanOverrideIds = detectOrphanOverrideIds(overrides, definitions);
  if (!overrides || orphanOverrideIds.length === 0) {
    return { nextOverrides: overrides ?? {}, orphanOverrideIds: [] };
  }
  const orphanSet = new Set(orphanOverrideIds);
  const nextOverrides: OrgCustomFieldOverrideMap = {};
  Object.entries(overrides).forEach(([fieldId, value]) => {
    if (!orphanSet.has(fieldId)) {
      nextOverrides[fieldId] = value;
    }
  });
  return { nextOverrides, orphanOverrideIds };
}

export function resolveEffectiveCustomFields(
  params: ResolveEffectiveCustomFieldsParams
): ResolveEffectiveCustomFieldsResult {
  const entityType = resolveOrgEntityType({
    nodeType: params.nodeType,
    entityType: params.entityType,
  });

  if (!entityType) {
    return { entityType: null, fields: [], orphanOverrideIds: [] };
  }

  const definitions = getDefinitionsForEntityType(params.definitions, entityType);
  const overrides = params.overrides ?? {};
  const orphanOverrideIds = detectOrphanOverrideIds(overrides, definitions);

  const fields = definitions.map((definition) => {
    const hasRawOverride = Object.prototype.hasOwnProperty.call(overrides, definition.id);
    const rawOverride = hasRawOverride ? overrides[definition.id] : undefined;
    const hasOverride = rawOverride !== undefined && isValueTypeCompatible(rawOverride, definition.type);
    const value = hasOverride ? rawOverride : definition.defaultValue;
    return {
      fieldId: definition.id,
      name: definition.name,
      type: definition.type,
      showOnCard: definition.showOnCard ?? false,
      ...(definition.defaultValue !== undefined ? { defaultValue: definition.defaultValue } : {}),
      ...(value !== undefined ? { value } : {}),
      hasOverride,
    };
  });

  return {
    entityType,
    fields,
    orphanOverrideIds,
  };
}

export function migrateLegacyCustomFields(
  params: MigrateLegacyCustomFieldsParams
): MigrateLegacyCustomFieldsResult {
  const nextDefinitions = [...params.definitions];
  const nextOverrides: OrgCustomFieldOverrideMap = { ...(params.existingOverrides ?? {}) };
  const createdDefinitions: OrgCustomFieldDefinition[] = [];
  const useLegacyValueAsDefault = params.useLegacyValueAsDefault === true;

  const byNormalizedName = new Map<string, OrgCustomFieldDefinition>();
  nextDefinitions
    .filter((definition) => definition.entityType === params.entityType)
    .forEach((definition) => {
      byNormalizedName.set(normalizeName(definition.name), definition);
    });

  const createDefinitionId = params.createDefinitionId ?? (() => crypto.randomUUID());
  let migratedCount = 0;

  params.legacyCustomFields.forEach((legacyField) => {
    const normalized = normalizeLegacyField(legacyField);
    if (!normalized) return;

    const key = normalizeName(normalized.name);
    let definition = byNormalizedName.get(key);

    if (!definition) {
      definition = {
        id: createDefinitionId(),
        entityType: params.entityType,
        name: normalized.name,
        type: normalized.type,
        ...(normalized.showOnCard !== undefined ? { showOnCard: normalized.showOnCard } : {}),
        ...(useLegacyValueAsDefault && isValuePresent(normalized.value)
          ? { defaultValue: normalized.value }
          : {}),
      };
      nextDefinitions.push(definition);
      createdDefinitions.push(definition);
      byNormalizedName.set(key, definition);
    }

    if (!Object.prototype.hasOwnProperty.call(nextOverrides, definition.id) && isValuePresent(normalized.value)) {
      nextOverrides[definition.id] = normalized.value;
    }

    migratedCount += 1;
  });

  const filteredDefinitions = getDefinitionsForEntityType(nextDefinitions, params.entityType);
  const { nextOverrides: cleanedOverrides, orphanOverrideIds } = removeOrphanOverrides(
    nextOverrides,
    filteredDefinitions
  );

  return {
    nextDefinitions,
    nextOverrides: cleanedOverrides,
    createdDefinitions,
    orphanOverrideIds,
    migratedCount,
  };
}
